import { useState, useEffect, useCallback } from 'react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/toastContext';
import { useConfirm } from '../components/ui/useConfirm';
import { Archive, RotateCcw, Trash2, HardDrive, Clock, Package } from 'lucide-react';

interface Agent {
  id: string;
  name: string;
}

interface ArchiveItem {
  id: string;
  agent_id: string;
  runtime_id: string;
  archive_reason: string;
  data_size_kb: number;
  archived_at: string;
}

export default function ArchiveManager() {
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [archives, setArchives] = useState<ArchiveItem[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [loadingArchives, setLoadingArchives] = useState(false);

  const loadAgents = useCallback(async () => {
    setLoadingAgents(true);
    try {
      const res = await apiClient.get<Agent[]>('/agents');
      setAgents(res.data);
    } catch (e) {
      console.error('Failed to load agents:', e);
      toast.error('加载 Agent 列表失败');
    } finally {
      setLoadingAgents(false);
    }
  }, [toast]);

  const loadArchives = useCallback(async (agentId: string) => {
    setLoadingArchives(true);
    try {
      const res = await apiClient.get<ArchiveItem[]>(`/agents/${agentId}/archives`);
      setArchives(res.data);
    } catch (e) {
      console.error('Failed to load archives:', e);
      toast.error('加载归档列表失败');
    } finally {
      setLoadingArchives(false);
    }
  }, [toast]);

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState 闭包
    const tick = () => {
      void loadAgents();
    };
    tick();
  }, [loadAgents]);

  // 渲染期同步：切换 Agent 时先清空旧归档（替代在 effect 中同步 setState）
  const [prevSelectedAgentId, setPrevSelectedAgentId] = useState(selectedAgentId);
  if (prevSelectedAgentId !== selectedAgentId) {
    setPrevSelectedAgentId(selectedAgentId);
    if (!selectedAgentId) {
      setArchives([]);
    }
  }

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState 闭包
    const tick = () => {
      if (selectedAgentId) {
        void loadArchives(selectedAgentId);
      }
    };
    tick();
  }, [selectedAgentId, loadArchives]);

  const handleRestore = async (archiveId: string) => {
    try {
      await apiClient.post(`/archives/${archiveId}/restore`);
      toast.success('归档恢复成功');
      if (selectedAgentId) {
        loadArchives(selectedAgentId);
      }
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } } };
      const detail = err.response?.data?.detail;
      console.error('Failed to restore archive:', e);
      toast.error(typeof detail === 'string' && detail ? detail : '归档恢复失败');
    }
  };

  const handleDelete = async (archive: ArchiveItem) => {
    if (!(await confirm({
      title: '确定永久删除该归档？',
      message: '此操作不可恢复。',
      danger: true,
      requireText: archive.archive_reason || '未填写原因',
      confirmText: '删除',
    }))) {
      return;
    }
    try {
      await apiClient.delete(`/archives/${archive.id}`);
      toast.success('归档已删除');
      if (selectedAgentId) {
        loadArchives(selectedAgentId);
      }
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } } };
      const detail = err.response?.data?.detail;
      console.error('Failed to delete archive:', e);
      toast.error(typeof detail === 'string' && detail ? detail : '归档删除失败');
    }
  };

  const formatDate = (value: string) => {
    try {
      return new Date(value).toLocaleString('zh-CN');
    } catch {
      return value;
    }
  };

  const formatSize = (kb: number) => {
    if (kb >= 1048576) {
      return `${(kb / 1048576).toFixed(2)} GB`;
    }
    if (kb >= 1024) {
      return `${(kb / 1024).toFixed(2)} MB`;
    }
    return `${kb} KB`;
  };

  if (loadingAgents) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <h1><Archive size={24} style={{ verticalAlign: 'middle', marginRight: 8 }} />归档中心</h1>
      <p className="text-muted mb-4">查看已下线 Agent 的归档数据，一键恢复或清理</p>

      <div className="card mb-4">
        <h3>选择 Agent</h3>
        <select
          className="form-control"
          value={selectedAgentId}
          onChange={(e) => setSelectedAgentId(e.target.value)}
          style={{ marginTop: 12 }}
        >
          <option value="">请选择 Agent</option>
          {agents.map((agent) => (
            <option key={agent.id} value={agent.id}>
              {agent.name}
            </option>
          ))}
        </select>
      </div>

      {selectedAgentId && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 style={{ margin: 0 }}>归档列表</h3>
            <span className="badge info">{archives.length} 个归档</span>
          </div>

          {loadingArchives ? (
            <div className="loading">
              <div className="spinner"></div>
            </div>
          ) : archives.length === 0 ? (
            <div className="empty">
              <Package size={40} color="var(--text-muted)" />
              <h3>暂无归档</h3>
              <p className="text-muted">该 Agent 暂无可用的运行时归档数据</p>
            </div>
          ) : (
            <div className="list" style={{ marginTop: 12 }}>
              {archives.map((archive) => (
                <div key={archive.id} className="list-item">
                  <div className="list-item-content">
                    <div className="flex items-center gap-2">
                      <Archive size={18} />
                      <span style={{ fontWeight: 600 }}>{archive.archive_reason || '未填写原因'}</span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-muted">
                      <span className="flex items-center gap-1">
                        <HardDrive size={14} />
                        {formatSize(archive.data_size_kb)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {formatDate(archive.archived_at)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="secondary"
                      style={{ padding: '6px 12px' }}
                      onClick={() => handleRestore(archive.id)}
                    >
                      <RotateCcw size={14} style={{ marginRight: 4 }} />
                      恢复
                    </button>
                    <button
                      className="danger"
                      style={{ padding: '6px 12px' }}
                      onClick={() => handleDelete(archive)}
                    >
                      <Trash2 size={14} style={{ marginRight: 4 }} />
                      删除
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {dialog}
    </div>
  );
}
